export type VisibilityEntry = {
  target: Element;
  isIntersecting: boolean;
  intersectionRatio: number;
};

export type VisibilityObserver = {
  observe: (element: Element) => void;
  disconnect: () => void;
};

export type VisibilityObserverFactory = (
  onChange: (entries: VisibilityEntry[]) => void,
) => VisibilityObserver;

type Dependencies<EventName extends string> = {
  track: (event: EventName) => boolean | void | Promise<boolean | void>;
  navigate: (path: string) => void;
  exposedEvent: EventName;
  clickedEvent: EventName;
  exposureRegistry?: Set<string>;
  createVisibilityObserver?: VisibilityObserverFactory;
  waitForTimeout?: () => Promise<void>;
  clickPersistenceCoordinator?: SupportCtaClickPersistenceCoordinator;
  generationSuccessPersistenceCoordinator?: GenerationSuccessPersistenceCoordinator;
};

export type GenerationSuccessPersistenceCoordinator = {
  begin: (resultId: string, outcome: Promise<boolean>) => void;
  afterPersistence: (
    resultId: string,
    effect: () => boolean | void | Promise<boolean | void>,
  ) => Promise<boolean>;
};

const GENERATION_PERSISTENCE_TIMEOUT_MS = 500;

export function createGenerationSuccessPersistenceCoordinator(
  waitForTimeout: () => Promise<void> = () =>
    new Promise((resolve) => globalThis.setTimeout(resolve, GENERATION_PERSISTENCE_TIMEOUT_MS)),
): GenerationSuccessPersistenceCoordinator {
  const pendingOutcomes = new Map<string, Promise<boolean>>();
  return {
    begin(resultId, outcome) {
      pendingOutcomes.set(resultId, outcome);
    },
    async afterPersistence(resultId, effect) {
      const outcome = pendingOutcomes.get(resultId);
      if (outcome) {
        const persisted = await Promise.race([
          outcome,
          waitForTimeout().then(() => false),
        ]);
        pendingOutcomes.delete(resultId);
        if (!persisted) return false;
      }
      return (await effect()) !== false;
    },
  };
}

export type SupportCtaClickPersistenceCoordinator = {
  begin: (outcome: Promise<boolean>) => void;
  afterClickPersistence: (effect: () => void | Promise<void>) => Promise<boolean>;
};

export function createSupportCtaClickPersistenceCoordinator(): SupportCtaClickPersistenceCoordinator {
  let pendingOutcome: Promise<boolean> | null = null;
  return {
    begin(outcome) {
      pendingOutcome = outcome;
    },
    async afterClickPersistence(effect) {
      const outcome = pendingOutcome;
      if (outcome) {
        const persisted = await outcome;
        if (pendingOutcome === outcome) pendingOutcome = null;
        if (!persisted) return false;
      }
      await effect();
      return true;
    },
  };
}

const supportCtaClickPersistence = createSupportCtaClickPersistenceCoordinator();
const generationSuccessPersistence = createGenerationSuccessPersistenceCoordinator();

export function createGenerationResultId(
  trackGenerationSucceeded?: () => boolean | void | Promise<boolean | void>,
) {
  const resultId = crypto.randomUUID();
  let tracked: boolean | void | Promise<boolean | void> = false;
  try {
    tracked = trackGenerationSucceeded?.() ?? false;
  } catch {
    tracked = false;
  }
  const persistenceOutcome = Promise.resolve(tracked)
    .then((persisted) => persisted !== false)
    .catch(() => false);
  generationSuccessPersistence.begin(resultId, persistenceOutcome);
  return resultId;
}

export function trackAfterSupportCtaClickPersistence(effect: () => void | Promise<void>) {
  return supportCtaClickPersistence.afterClickPersistence(effect);
}

// IDs live only for this browser tab's module lifetime and one UUID is added per
// generated result. Keeping them preserves remount dedupe without persistent data.
const exposedResultIds = new Set<string>();
const CLICK_PERSISTENCE_TIMEOUT_MS = 500;

function createDefaultVisibilityObserver(
  onChange: (entries: VisibilityEntry[]) => void,
): VisibilityObserver {
  if (typeof IntersectionObserver !== "undefined") {
    const observer = new IntersectionObserver((entries) => onChange(entries));
    return {
      observe: (element) => observer.observe(element),
      disconnect: () => observer.disconnect(),
    };
  }

  let element: Element | null = null;
  const checkVisibility = () => {
    if (!element || typeof window === "undefined") return;
    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const visibleWidth = Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0);
    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
    const isIntersecting = visibleWidth > 0 && visibleHeight > 0;
    const elementArea = Math.max(rect.width * rect.height, 1);
    const intersectionRatio = isIntersecting
      ? Math.min(1, (visibleWidth * visibleHeight) / elementArea)
      : 0;
    onChange([{ target: element, isIntersecting, intersectionRatio }]);
  };

  return {
    observe: (nextElement) => {
      element = nextElement;
      window.addEventListener("scroll", checkVisibility, { passive: true });
      window.addEventListener("resize", checkVisibility);
      checkVisibility();
    },
    disconnect: () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("scroll", checkVisibility);
        window.removeEventListener("resize", checkVisibility);
      }
      element = null;
    },
  };
}

export function createPostGenerationSupportCtaAnalytics<EventName extends string>({
  track,
  navigate,
  exposedEvent,
  clickedEvent,
  exposureRegistry = exposedResultIds,
  createVisibilityObserver = createDefaultVisibilityObserver,
  waitForTimeout = () => new Promise((resolve) => globalThis.setTimeout(resolve, CLICK_PERSISTENCE_TIMEOUT_MS)),
  clickPersistenceCoordinator = supportCtaClickPersistence,
  generationSuccessPersistenceCoordinator = generationSuccessPersistence,
}: Dependencies<EventName>) {
  const hasValidResult = (resultId: string | null, isValid: boolean): resultId is string =>
    isValid && typeof resultId === "string" && resultId.length > 0;

  return {
    observe(element: Element, resultId: string | null, isValid: boolean) {
      if (!hasValidResult(resultId, isValid) || exposureRegistry.has(resultId)) {
        return () => undefined;
      }

      const observer = createVisibilityObserver((entries) => {
        const isVisible = entries.some((entry) =>
          entry.target === element && entry.isIntersecting && entry.intersectionRatio > 0
        );
        if (!isVisible || exposureRegistry.has(resultId)) return;

        exposureRegistry.add(resultId);
        observer.disconnect();
        void generationSuccessPersistenceCoordinator.afterPersistence(
          resultId,
          () => track(exposedEvent),
        ).catch(() => undefined);
      });
      observer.observe(element);
      return () => observer.disconnect();
    },

    async click(resultId: string | null, isValid: boolean) {
      if (!hasValidResult(resultId, isValid)) return;

      let tracked: boolean | void | Promise<boolean | void>;
      try {
        tracked = track(clickedEvent);
      } catch {
        tracked = false;
      }
      const persistenceOutcome = Promise.resolve(tracked)
        .then((persisted) => persisted !== false)
        .catch(() => false);
      clickPersistenceCoordinator.begin(persistenceOutcome);
      await Promise.race([persistenceOutcome, waitForTimeout()]);
      navigate("/create/support");
    },
  };
}
